import React, { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { UploadCloud, FileText, Loader2, X, Sparkles, AlertTriangle, Layers } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { extractDatasheet, createProduct, getCategories } from "@/lib/api";
import { NUM_FIELDS, TXT_FIELDS, BOOL_FIELDS, numLabel } from "@/lib/fields";
import { num, fmt } from "@/lib/calc";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

export default function UploadDialog({ open, onClose, subcategory, onSaved }) {
  const { t, tc } = useApp();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [cats, setCats] = useState([]);
  const inputRef = useRef();
  const est = new Set((data && data.estimated_fields) || []);

  useEffect(() => {
    if (open && cats.length === 0) getCategories().then(setCats).catch(() => {});
  }, [open]);

  const currentCat = cats.find((c) => (c.subcategories || []).some((s) => s.id === data?.subcategory))?.id || "";
  const subsForCat = (catId) => (cats.find((c) => c.id === catId)?.subcategories || []);
  const onCatChange = (catId) => {
    const subs = subsForCat(catId);
    setField("subcategory", subs[0]?.id || "");
  };

  const reset = () => { setFile(null); setData(null); setLoading(false); };
  const close = () => { reset(); onClose(); };

  const onExtract = async () => {
    if (!file) return toast.error(t("fileRequired"));
    setLoading(true);
    try {
      const res = await extractDatasheet(file, subcategory);
      const specs = res.specs || {};
      const w = num(specs.bag_weight_kg);
      // se la scheda dà solo il prezzo al sacco, ricava il prezzo al kg dal peso del sacco
      if (!num(specs.price_per_kg) && num(specs.price_per_bag) && w) {
        specs.price_per_kg = Math.round((num(specs.price_per_bag) / w) * 10000) / 10000;
      }
      delete specs.price_per_bag;
      setData({ ...res, specs });
      (res.warnings || []).forEach((msg) => toast.warning(msg, { duration: 8000 }));
    } catch (e) {
      toast.error(t("extractError"));
    } finally {
      setLoading(false);
    }
  };

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setSpec = (k, v) => setData((d) => ({ ...d, specs: { ...d.specs, [k]: v } }));

  const onSave = async () => {
    if (!data.name || !data.brand) return toast.error("Name / Brand");
    const specs = { ...data.specs };
    const derivedBag = num(specs.price_per_kg) * num(specs.bag_weight_kg);
    if (derivedBag > 0) specs.price_per_bag = Math.round(derivedBag * 100) / 100;
    try {
      const product = await createProduct({
        name: data.name, brand: data.brand, subcategory: data.subcategory,
        applications: Array.from(new Set([data.subcategory, ...(data.applications || [])].filter(Boolean))),
        description: data.description || "", specs,
        source_file: data.source_file || null,
      });
      toast.success(t("savedOk"));
      (product.warnings || []).forEach((msg) => toast.warning(msg, { duration: 8000 }));
      onSaved && onSaved(product);
      close();
    } catch (e) {
      toast.error("Error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-2xl rounded-none border-border p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-border px-5 py-3">
          <DialogTitle className="font-display text-lg tracking-tight">{t("uploadTitle")}</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto p-5">
          {!data ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t("uploadHint")}</p>
              <input
                ref={inputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                className="hidden" data-testid="file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <button
                data-testid="dropzone"
                onClick={() => inputRef.current?.click()}
                className="flex w-full flex-col items-center justify-center gap-3 border-2 border-dashed border-border bg-surface py-12 hover:border-primary"
              >
                {file ? (
                  <>
                    <FileText className="h-8 w-8 text-primary" />
                    <span className="font-mono text-sm">{file.name}</span>
                  </>
                ) : (
                  <>
                    <UploadCloud className="h-8 w-8 text-muted-foreground" />
                    <span className="text-sm">{t("uploadDrop")}</span>
                    <span className="label-caps text-muted-foreground">{t("uploadFormats")}</span>
                  </>
                )}
              </button>

              {loading && (
                <div className="flex items-center gap-2 border border-border bg-primary/10 px-4 py-3 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  {t("extracting")}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-none" onClick={close}>{t("cancel")}</Button>
                <Button data-testid="extract-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90" disabled={!file || loading} onClick={onExtract}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("uploadTitle")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm font-semibold">{t("reviewExtracted")}</p>
              {data.duplicate && (
                <div className="flex items-start gap-2 border border-destructive/50 bg-destructive/10 px-3 py-2.5 text-xs" data-testid="duplicate-warning">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <span>
                    {data.duplicate.exact ? t("dupWarn") : t("dupSimilar")} <b>{data.duplicate.brand} · {data.duplicate.name}</b>
                    {!data.duplicate.exact && data.duplicate.similar_specs && " ✓ valori simili"}
                  </span>
                </div>
              )}
              <div className="border border-info/40 bg-info/10 px-3 py-3" data-testid="review-taxonomy">
                <div className="mb-2 flex items-center gap-2 text-xs text-info">
                  <Sparkles className="h-3.5 w-3.5 shrink-0" />
                  <span>{t("autoCategory")} — {t("editCategoryHint")}</span>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1.5">
                    <span className="label-caps text-muted-foreground">{t("category")}</span>
                    <Select value={currentCat} onValueChange={onCatChange}>
                      <SelectTrigger data-testid="review-category" className="rounded-none border-input bg-surface font-mono text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {cats.map((c) => <SelectItem key={c.id} value={c.id}>{tc(c.id)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="label-caps text-muted-foreground">{t("subcategory")}</span>
                    <Select value={data.subcategory || ""} onValueChange={(v) => setField("subcategory", v)}>
                      <SelectTrigger data-testid="review-subcategory" className="rounded-none border-input bg-surface font-mono text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {subsForCat(currentCat).map((s) => <SelectItem key={s.id} value={s.id}>{tc(s.id)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </label>
                </div>
              </div>
              {(data.applications || []).filter((a) => a !== data.subcategory).length > 0 && (
                <div className="flex flex-wrap items-center gap-2 border border-sky-400/40 bg-sky-400/10 px-3 py-2 text-xs" data-testid="review-applications">
                  <Layers className="h-4 w-4 shrink-0 text-sky-400" />
                  <span className="font-semibold">{t("multiUseLabel")}:</span>
                  <span className="rounded-full bg-sky-400/20 px-2 py-0.5 font-mono">{tc(data.subcategory)}</span>
                  {(data.applications || []).filter((a) => a !== data.subcategory).map((a) => (
                    <button key={a} type="button" title={t("removeUse")}
                      onClick={() => setData((d) => ({ ...d, applications: (d.applications || []).filter((x) => x !== a) }))}
                      className="flex items-center gap-1 rounded-full bg-sky-400/20 px-2 py-0.5 font-mono transition-colors hover:bg-red-500/20">
                      {tc(a)} <X className="h-3 w-3" />
                    </button>
                  ))}
                </div>
              )}
              {est.size > 0 && (
                <div className="flex items-start gap-2 border border-primary/40 bg-primary/10 px-3 py-2.5 text-xs">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>{t("aiEstimatedHint")}</span>
                </div>
              )}
              <p className="border-t border-border pt-2 text-[10px] leading-snug text-muted-foreground/70" data-testid="ai-disclaimer">{t("disclaimerShort")}</p>
              <div className="grid grid-cols-2 gap-3">
                <FormText label={t("brand")} value={data.brand} onChange={(v) => setField("brand", v)} testid="review-brand" />
                <FormText label={t("product")} value={data.name} onChange={(v) => setField("name", v)} testid="review-name" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                {TXT_FIELDS.map(([k, lk]) => (
                  <FormText key={k} label={t(lk)} value={data.specs?.[k] || ""} onChange={(v) => setSpec(k, v)} estimated={est.has(k)} />
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {NUM_FIELDS.map(([k, lk]) => (
                  <FormNum key={k} label={numLabel(t, k, lk)} field={k} value={data.specs?.[k]} onChange={(v) => setSpec(k, v)} estimated={est.has(k)} />
                ))}
                <label className="flex flex-col gap-1.5">
                  <span className="label-caps text-muted-foreground truncate">{t("priceBag")}</span>
                  <div className="flex items-center border border-dashed border-input bg-surface">
                    <span className="w-full px-2.5 py-2 font-mono text-sm text-muted-foreground" data-testid="review-price-bag">
                      {num(data.specs?.price_per_kg) * num(data.specs?.bag_weight_kg) > 0
                        ? fmt(num(data.specs?.price_per_kg) * num(data.specs?.bag_weight_kg), 2)
                        : "—"}
                    </span>
                    <span className="px-1.5 font-mono text-[10px] text-muted-foreground/70">CHF</span>
                  </div>
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                {BOOL_FIELDS.map((k) => {
                  const active = data.specs?.[k] === true || data.specs?.[k] === "true";
                  return (
                    <button
                      key={k}
                      onClick={() => setSpec(k, !active)}
                      className={`border px-2.5 py-1.5 text-xs font-medium ${active ? "border-primary bg-primary/15 text-foreground" : "border-border text-muted-foreground"} ${est.has(k) ? "ring-1 ring-primary/60" : ""}`}
                    >
                      {t(k)}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" className="rounded-none" onClick={reset}>
                  <X className="mr-1 h-4 w-4" /> {t("cancel")}
                </Button>
                <Button data-testid="save-product-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90" onClick={onSave}>
                  {t("saveProduct")}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FormText({ label, value, onChange, testid, estimated }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="label-caps text-muted-foreground">{label}</span>
      <input
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`border bg-surface px-2.5 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-primary ${estimated ? "border-primary/60 bg-primary/5" : "border-input"}`}
      />
    </label>
  );
}

function FormNum({ label, field, value, onChange, estimated }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="label-caps text-muted-foreground truncate">{label}</span>
      <input
        type="number"
        data-testid={`review-${field}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))}
        className={`border bg-surface px-2.5 py-2 font-mono text-sm outline-none focus:ring-1 focus:ring-primary ${estimated ? "border-primary/60 bg-primary/5" : "border-input"}`}
      />
    </label>
  );
}
